import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from '../services/axios';
import Sidebar from '../components/Sidebar';
import Modal from '../components/Modal';
import { formatApiDate } from '../utils/date';

function VerCliente() {
  const navigate = useNavigate();
  const { id } = useParams();
  const [cliente, setCliente] = useState(null);
  const [lecturas, setLecturas] = useState([]);
  const [boletas, setBoletas] = useState([]);
  const [clientesDisponibles, setClientesDisponibles] = useState([]);
  const [vinculadosMedidor, setVinculadosMedidor] = useState([]);
  const [previewDivision, setPreviewDivision] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadingVinculos, setLoadingVinculos] = useState(false);
  const [loadingDivision, setLoadingDivision] = useState(false);
  const [usuarioHijoSeleccionado, setUsuarioHijoSeleccionado] = useState('');
  const [showModalMedidorRoto, setShowModalMedidorRoto] = useState(false);
  const [showModalAsignarMedidor, setShowModalAsignarMedidor] = useState(false);
  const [nuevoMedidor, setNuevoMedidor] = useState({
    numeroSerie: '',
    lecturaInicial: 0,
  });
  const [modalConfig, setModalConfig] = useState({
    isOpen: false,
    type: 'info',
    title: '',
    message: '',
    onConfirm: null
  });

  useEffect(() => {
    cargarDatos();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const cargarDatos = async () => {
    try {
      const [clienteRes, boletasRes, vinculadosRes, usuariosRes, previewRes] = await Promise.all([
        axios.get(`/usuarios/${id}`),
        axios.get(`/boletas/usuario/${id}`).catch(() => ({ data: [] })),
        axios.get(`/usuarios/${id}/vinculados`).catch(() => ({ data: [] })),
        axios.get('/usuarios').catch(() => ({ data: [] })),
        axios.get(`/tarifario/dividir-boletas-pendientes/${id}/preview`).catch(() => ({ data: null })),
      ]);
      
      setCliente(clienteRes.data);
      
      // Si hay medidor activo, cargar sus lecturas
      if (clienteRes.data.medidor?.id) {
        const lecturasRes = await axios.get(`/lecturas?medidorId=${clienteRes.data.medidor.id}`).catch(() => ({ data: [] }));
        setLecturas(lecturasRes.data || []);
      } else {
        setLecturas([]);
      }
      
      setBoletas(boletasRes.data || []);
      setVinculadosMedidor(vinculadosRes.data || []);
      setClientesDisponibles((usuariosRes.data || []).filter((usuario) => usuario.rol === 'cliente' && usuario.activo !== false));
      setPreviewDivision(previewRes.data || null);
    } catch (error) {
      console.error('Error al cargar datos:', error);
      setModalConfig({
        isOpen: true,
        type: 'error',
        title: 'Error al Cargar',
        message: 'No se pudo cargar la información del cliente.',
        onConfirm: () => navigate('/administrativo/clientes')
      });
    } finally {
      setLoading(false);
    }
  };

  const formatearDecimal = (valor) => Number(valor || 0).toLocaleString('es-AR', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

  const vinculadosIds = new Set(vinculadosMedidor.map((vinculo) => vinculo.usuarioHijo?.id));
  const clientesParaVincular = clientesDisponibles
    .filter((usuario) => Number(usuario.id) !== Number(id))
    .filter((usuario) => !vinculadosIds.has(usuario.id))
    .filter((usuario) => !usuario.medidor?.id);

  const cargarVinculosYPreview = async () => {
    const [vinculadosRes, previewRes] = await Promise.all([
      axios.get(`/usuarios/${id}/vinculados`).catch(() => ({ data: [] })),
      axios.get(`/tarifario/dividir-boletas-pendientes/${id}/preview`).catch(() => ({ data: null })),
    ]);

    setVinculadosMedidor(vinculadosRes.data || []);
    setPreviewDivision(previewRes.data || null);
  };

  const handleVincularUsuario = async () => {
    if (!usuarioHijoSeleccionado) {
      setModalConfig({
        isOpen: true,
        type: 'warning',
        title: 'Usuario requerido',
        message: 'Seleccione un usuario sin medidor para vincularlo.',
        onConfirm: () => setModalConfig({ ...modalConfig, isOpen: false })
      });
      return;
    }

    setLoadingVinculos(true);
    try {
      await axios.post(`/usuarios/${id}/vinculados`, {
        usuarioHijoId: Number(usuarioHijoSeleccionado),
      });
      setUsuarioHijoSeleccionado('');
      await cargarVinculosYPreview();
      setModalConfig({
        isOpen: true,
        type: 'success',
        title: 'Usuario vinculado',
        message: 'El usuario fue vinculado al medidor padre.',
        onConfirm: () => setModalConfig({ ...modalConfig, isOpen: false })
      });
    } catch (error) {
      setModalConfig({
        isOpen: true,
        type: 'error',
        title: 'No se pudo vincular',
        message: error.response?.data?.message || error.message || 'Ocurrió un error inesperado.',
        onConfirm: () => setModalConfig({ ...modalConfig, isOpen: false })
      });
    } finally {
      setLoadingVinculos(false);
    }
  };

  const handleDesvincularUsuario = (vinculo) => {
    const hijo = vinculo.usuarioHijo;
    setModalConfig({
      isOpen: true,
      type: 'warning',
      title: 'Desvincular usuario',
      message: `Se quitara a ${hijo?.padron || ''} ${hijo?.nombre || ''} de la division del medidor. Las boletas ya generadas no se modificaran automaticamente.`,
      onConfirm: async () => {
        setLoadingVinculos(true);
        try {
          await axios.delete(`/usuarios/${id}/vinculados/${vinculo.id}`);
          await cargarVinculosYPreview();
          setModalConfig({
            isOpen: true,
            type: 'success',
            title: 'Usuario desvinculado',
            message: 'La vinculacion fue dada de baja.',
            onConfirm: () => setModalConfig({ ...modalConfig, isOpen: false })
          });
        } catch (error) {
          setModalConfig({
            isOpen: true,
            type: 'error',
            title: 'No se pudo desvincular',
            message: error.response?.data?.message || error.message || 'Ocurrió un error inesperado.',
            onConfirm: () => setModalConfig({ ...modalConfig, isOpen: false })
          });
        } finally {
          setLoadingVinculos(false);
        }
      },
      onCancel: () => setModalConfig({ ...modalConfig, isOpen: false })
    });
  };

  const handleDividirBoletasPendientes = () => {
    const periodos = previewDivision?.periodos || [];
    const vinculados = previewDivision?.vinculados || [];
    const participantes = previewDivision?.participantes?.length || (vinculados.length > 0 ? vinculados.length + 1 : 0);

    if (vinculados.length === 0 || periodos.length === 0) {
      setModalConfig({
        isOpen: true,
        type: 'warning',
        title: 'Sin boletas para dividir',
        message: vinculados.length === 0
          ? 'Primero vincule al menos un usuario al medidor.'
          : 'No hay boletas pendientes o vencidas del padre para dividir.',
        onConfirm: () => setModalConfig({ ...modalConfig, isOpen: false })
      });
      return;
    }

    const detalle = periodos
      .slice(0, 6)
      .map((periodo) => `${periodo.mes}/${periodo.anio}: ${formatearDecimal(periodo.consumoOriginal)} m³ en ${participantes} partes`)
      .join('\n');

    setModalConfig({
      isOpen: true,
      type: 'warning',
      title: 'Dividir boletas impagas',
      message: `Se anularan las boletas impagas originales del padre y se generaran boletas independientes para el padre y sus usuarios vinculados.\n\n${detalle}${periodos.length > 6 ? `\nY ${periodos.length - 6} periodos mas...` : ''}\n\nSolo se divide el consumo. El monto de cada boleta se recalculara con el tarifario segun el consumo asignado.\n\nNo se procesaran boletas pagadas, procesando o en plan de pago.`,
      confirmText: 'Dividir boletas',
      onConfirm: async () => {
        setLoadingDivision(true);
        try {
          const response = await axios.post(`/tarifario/dividir-boletas-pendientes/${id}`);
          const resultado = response.data || {};
          await cargarDatos();
          setModalConfig({
            isOpen: true,
            type: 'success',
            title: 'Division completada',
            message: `Boletas divididas: ${resultado.boletasDivididas || 0}\nBoletas generadas: ${resultado.boletasGeneradas || resultado.boletasHijasGeneradas || 0}`,
            onConfirm: () => setModalConfig({ ...modalConfig, isOpen: false })
          });
        } catch (error) {
          setModalConfig({
            isOpen: true,
            type: 'error',
            title: 'No se pudo dividir',
            message: error.response?.data?.message || error.message || 'Ocurrió un error inesperado.',
            onConfirm: () => setModalConfig({ ...modalConfig, isOpen: false })
          });
        } finally {
          setLoadingDivision(false);
        }
      },
      onCancel: () => setModalConfig({ ...modalConfig, isOpen: false })
    });
  };

  const handleDeclararMedidorRoto = async () => {
    try {
      await axios.put(`/medidores/${cliente.medidor.id}/dar-baja`, {
        motivo: 'Medidor roto',
      });
      
      setShowModalMedidorRoto(false);
      setModalConfig({
        isOpen: true,
        type: 'success',
        title: '¡Medidor Declarado como Roto!',
        message: 'El medidor ha sido dado de baja exitosamente.\n\nEl cliente quedará sin medidor activo.\nSe le cobrará la tarifa mínima hasta que se asigne un nuevo medidor.',
        onConfirm: () => {
          setModalConfig({ ...modalConfig, isOpen: false });
          cargarDatos();
        }
      });
    } catch (error) {
      console.error('Error al declarar medidor roto:', error);
      setModalConfig({
        isOpen: true,
        type: 'error',
        title: 'Error al Declarar Medidor Roto',
        message: error.response?.data?.message || error.message || 'Ocurrió un error inesperado.',
        onConfirm: () => setModalConfig({ ...modalConfig, isOpen: false })
      });
    }
  };

  const handleAsignarMedidor = async () => {
    if (!nuevoMedidor.numeroSerie.trim()) {
      setModalConfig({
        isOpen: true,
        type: 'warning',
        title: 'Número de Serie Requerido',
        message: 'Por favor ingrese el número de serie del medidor.',
        onConfirm: () => setModalConfig({ ...modalConfig, isOpen: false })
      });
      return;
    }

    try {
      await axios.post(`/medidores/asignar/${id}`, {
        numeroSerie: nuevoMedidor.numeroSerie,
        lecturaInicial: parseInt(nuevoMedidor.lecturaInicial) || 0,
      });
      
      setShowModalAsignarMedidor(false);
      setNuevoMedidor({ numeroSerie: '', lecturaInicial: 0 });
      setModalConfig({
        isOpen: true,
        type: 'success',
        title: '¡Medidor Asignado!',
        message: 'El medidor ha sido asignado exitosamente.\n\nYa se pueden registrar lecturas para este cliente.',
        onConfirm: () => {
          setModalConfig({ ...modalConfig, isOpen: false });
          cargarDatos();
        }
      });
    } catch (error) {
      console.error('Error al asignar medidor:', error);
      setModalConfig({
        isOpen: true,
        type: 'error',
        title: 'Error al Asignar Medidor',
        message: error.response?.data?.message || error.message || 'Ocurrió un error inesperado.',
        onConfirm: () => setModalConfig({ ...modalConfig, isOpen: false })
      });
    }
  };

  const handleDarBajaServicio = () => {
    setModalConfig({
      isOpen: true,
      type: 'warning',
      title: 'Dar de baja el servicio',
      message: 'No se generarán más boletas automáticas y el servicio quedará marcado como desconectado. ¿Desea continuar?',
      onConfirm: async () => {
        try {
          await axios.put(`/usuarios/${id}/baja-servicio`);
          setModalConfig({
            isOpen: true,
            type: 'success',
            title: 'Servicio dado de baja',
            message: 'El servicio fue desactivado. No se generarán nuevas boletas ni lecturas hasta reactivarlo.',
            onConfirm: () => {
              setModalConfig({ ...modalConfig, isOpen: false });
              cargarDatos();
            }
          });
        } catch (error) {
          setModalConfig({
            isOpen: true,
            type: 'error',
            title: 'Error al dar de baja',
            message: error.response?.data?.message || error.message || 'No se pudo dar de baja el servicio.',
            onConfirm: () => setModalConfig({ ...modalConfig, isOpen: false })
          });
        }
      },
      onCancel: () => setModalConfig({ ...modalConfig, isOpen: false })
    });
  };

  const handleReactivarServicio = () => {
    setModalConfig({
      isOpen: true,
      type: 'warning',
      title: 'Reactivar servicio',
      message: 'El servicio volverá a estar activo y podrá generar boletas nuevamente. ¿Confirmar?',
      onConfirm: async () => {
        try {
          await axios.put(`/usuarios/${id}/reactivar-servicio`);
          setModalConfig({
            isOpen: true,
            type: 'success',
            title: 'Servicio reactivado',
            message: 'El servicio fue reactivado correctamente.',
            onConfirm: () => {
              setModalConfig({ ...modalConfig, isOpen: false });
              cargarDatos();
            }
          });
        } catch (error) {
          setModalConfig({
            isOpen: true,
            type: 'error',
            title: 'Error al reactivar',
            message: error.response?.data?.message || error.message || 'No se pudo reactivar el servicio.',
            onConfirm: () => setModalConfig({ ...modalConfig, isOpen: false })
          });
        }
      },
      onCancel: () => setModalConfig({ ...modalConfig, isOpen: false })
    });
  };

  if (loading) {
    return (
      <div className="flex h-screen">
        <Sidebar />
        <div className="flex-1 flex items-center justify-center">
          <div className="text-center">
            <span className="material-symbols-outlined text-primary text-6xl animate-spin">refresh</span>
            <p className="text-[#617c89] mt-4">Cargando información del cliente...</p>
          </div>
        </div>
      </div>
    );
  }

  if (!cliente) {
    return (
      <div className="flex h-screen">
        <Sidebar />
        <div className="flex-1 flex items-center justify-center">
          <div className="text-center">
            <span className="material-symbols-outlined text-red-500 text-6xl">error</span>
            <p className="text-[#617c89] mt-4">Cliente no encontrado</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex h-screen">
      <Sidebar />
      
      <main className="flex-1 overflow-y-auto bg-background-light dark:bg-background-dark">
        <div className="p-8">
          {/* Header */}
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-4">
              <button
                onClick={() => navigate('/administrativo/clientes')}
                className="flex items-center justify-center h-10 w-10 rounded-lg border border-gray-300 dark:border-gray-600 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
              >
                <span className="material-symbols-outlined">arrow_back</span>
              </button>
              <div>
                <h1 className="text-[#111618] dark:text-white text-4xl font-black leading-tight tracking-[-0.033em]">
                  {cliente.nombre}
                </h1>
                <p className="text-[#617c89] dark:text-gray-400 text-sm mt-1">
                  Padrón: {cliente.padron}
                </p>
              </div>
            </div>
            <button
              onClick={() => navigate(`/administrativo/clientes/editar/${id}`)}
              className="flex items-center justify-center gap-2 h-10 px-4 bg-primary text-white rounded-lg font-bold hover:bg-primary/90 transition-colors"
            >
              <span className="material-symbols-outlined">edit</span>
              Editar
            </button>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Información Principal */}
            <div className="lg:col-span-2 space-y-6">
              {/* Datos Personales */}
              <div className="bg-white dark:bg-background-dark dark:border dark:border-gray-700 rounded-xl shadow-sm p-6">
                <h2 className="text-lg font-bold text-[#111618] dark:text-white mb-4 flex items-center gap-2">
                  <span className="material-symbols-outlined text-primary">person</span>
                  Información Personal
                </h2>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-xs text-[#617c89] dark:text-gray-400 uppercase mb-1">CUIT/DNI</p>
                    <p className="text-sm font-medium text-[#111618] dark:text-white">{cliente.cuit || '-'}</p>
                  </div>
                  <div>
                    <p className="text-xs text-[#617c89] dark:text-gray-400 uppercase mb-1">Email</p>
                    <p className="text-sm font-medium text-[#111618] dark:text-white">{cliente.email || '-'}</p>
                  </div>
                  <div>
                    <p className="text-xs text-[#617c89] dark:text-gray-400 uppercase mb-1">Tipo</p>
                    <p className="text-sm font-medium text-[#111618] dark:text-white capitalize">{cliente.tipo || '-'}</p>
                  </div>
                  <div>
                    <p className="text-xs text-[#617c89] dark:text-gray-400 uppercase mb-1">Orden (Tarifa)</p>
                    <p className="text-sm font-medium text-[#111618] dark:text-white">{cliente.orden || '-'}</p>
                  </div>
                </div>
              </div>

              {/* Ubicación */}
              <div className="bg-white dark:bg-background-dark dark:border dark:border-gray-700 rounded-xl shadow-sm p-6">
                <h2 className="text-lg font-bold text-[#111618] dark:text-white mb-4 flex items-center gap-2">
                  <span className="material-symbols-outlined text-primary">home</span>
                  Ubicación
                </h2>
                <div className="grid grid-cols-2 gap-4">
                  <div className="col-span-2">
                    <p className="text-xs text-[#617c89] dark:text-gray-400 uppercase mb-1">Dirección</p>
                    <p className="text-sm font-medium text-[#111618] dark:text-white">{cliente.direccion}</p>
                  </div>
                  <div>
                    <p className="text-xs text-[#617c89] dark:text-gray-400 uppercase mb-1">Localidad</p>
                    <p className="text-sm font-medium text-[#111618] dark:text-white">{cliente.localidad || '-'}</p>
                  </div>
                  <div>
                    <p className="text-xs text-[#617c89] dark:text-gray-400 uppercase mb-1">Código Postal</p>
                    <p className="text-sm font-medium text-[#111618] dark:text-white">{cliente.codigoPostal || '-'}</p>
                  </div>
                  <div>
                    <p className="text-xs text-[#617c89] dark:text-gray-400 uppercase mb-1">Zona</p>
                    <p className="text-sm font-medium text-[#111618] dark:text-white">{cliente.zona?.nombre || '-'}</p>
                  </div>
                  <div>
                    <p className="text-xs text-[#617c89] dark:text-gray-400 uppercase mb-1">Medidor</p>
                    <p className="text-sm font-medium text-[#111618] dark:text-white">
                      {cliente.medidor?.numeroSerie || 'Sin medidor asignado'}
                    </p>
                  </div>
                </div>
              </div>

              {cliente.medidor && !cliente.servicio_dado_de_baja && (
                <div className="bg-white dark:bg-background-dark dark:border dark:border-gray-700 rounded-xl shadow-sm p-6">
                  <div className="flex items-start justify-between gap-4 mb-4">
                    <div>
                      <h2 className="text-lg font-bold text-[#111618] dark:text-white flex items-center gap-2">
                        <span className="material-symbols-outlined text-primary">hub</span>
                        Usuarios vinculados al medidor
                      </h2>
                      <p className="text-sm text-[#617c89] dark:text-gray-400 mt-1">
                        Medidor padre: {cliente.medidor.numeroSerie}
                      </p>
                    </div>
                    <div className="text-right">
                      <p className="text-xs text-[#617c89] dark:text-gray-400 uppercase">Vinculados</p>
                      <p className="text-2xl font-black text-[#111618] dark:text-white">{vinculadosMedidor.length}</p>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-[1fr_auto] gap-3 mb-5">
                    <select
                      value={usuarioHijoSeleccionado}
                      onChange={(e) => setUsuarioHijoSeleccionado(e.target.value)}
                      className="h-11 px-3 rounded-lg bg-[#f0f3f4] dark:bg-gray-700 border-none text-[#111618] dark:text-white focus:outline-none focus:ring-2 focus:ring-primary"
                      disabled={loadingVinculos}
                    >
                      <option value="">Seleccionar cliente sin medidor</option>
                      {clientesParaVincular.map((usuario) => (
                        <option key={usuario.id} value={usuario.id}>
                          {usuario.padron || 'Sin padron'} - {usuario.nombre}
                        </option>
                      ))}
                    </select>
                    <button
                      onClick={handleVincularUsuario}
                      disabled={loadingVinculos || !usuarioHijoSeleccionado}
                      className="h-11 px-4 bg-primary text-white rounded-lg font-bold hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center justify-center gap-2"
                    >
                      <span className="material-symbols-outlined text-base">link</span>
                      Vincular
                    </button>
                  </div>

                  {vinculadosMedidor.length === 0 ? (
                    <div className="rounded-lg border border-dashed border-gray-300 dark:border-gray-700 p-4 text-center">
                      <p className="text-sm text-[#617c89] dark:text-gray-400">No hay usuarios vinculados a este medidor.</p>
                    </div>
                  ) : (
                    <div className="overflow-x-auto rounded-lg border border-gray-100 dark:border-gray-700">
                      <table className="w-full">
                        <thead className="bg-[#f8fafb] dark:bg-gray-800">
                          <tr>
                            <th className="text-left text-xs font-medium text-[#617c89] uppercase px-3 py-2">Padrón</th>
                            <th className="text-left text-xs font-medium text-[#617c89] uppercase px-3 py-2">Usuario</th>
                            <th className="text-left text-xs font-medium text-[#617c89] uppercase px-3 py-2">Zona</th>
                            <th className="text-right text-xs font-medium text-[#617c89] uppercase px-3 py-2">Acción</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y dark:divide-gray-700">
                          {vinculadosMedidor.map((vinculo) => (
                            <tr key={vinculo.id}>
                              <td className="px-3 py-3 text-sm font-bold text-[#111618] dark:text-white">
                                {vinculo.usuarioHijo?.padron || '-'}
                              </td>
                              <td className="px-3 py-3 text-sm text-[#111618] dark:text-white">
                                {vinculo.usuarioHijo?.nombre || '-'}
                              </td>
                              <td className="px-3 py-3 text-sm text-[#617c89] dark:text-gray-400">
                                {vinculo.usuarioHijo?.zona?.nombre || '-'}
                              </td>
                              <td className="px-3 py-3 text-right">
                                <button
                                  onClick={() => handleDesvincularUsuario(vinculo)}
                                  disabled={loadingVinculos}
                                  className="inline-flex items-center justify-center h-9 w-9 rounded-lg border border-red-200 text-red-600 hover:bg-red-50 disabled:opacity-50 transition-colors"
                                  title="Desvincular"
                                >
                                  <span className="material-symbols-outlined text-base">link_off</span>
                                </button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}

                  <div className="mt-5 rounded-lg bg-[#f8fafb] dark:bg-gray-800 border border-gray-100 dark:border-gray-700 p-4">
                    <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
                      <div>
                        <p className="text-sm font-bold text-[#111618] dark:text-white">Boletas impagas para dividir</p>
                        <p className="text-sm text-[#617c89] dark:text-gray-400">
                          {(previewDivision?.periodos || []).length} períodos pendientes o vencidos · se divide consumo, no monto
                        </p>
                      </div>
                      <button
                        onClick={handleDividirBoletasPendientes}
                        disabled={loadingDivision || vinculadosMedidor.length === 0 || (previewDivision?.periodos || []).length === 0}
                        className="h-11 px-4 bg-[#111618] text-white rounded-lg font-bold hover:bg-black disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center justify-center gap-2"
                      >
                        <span className="material-symbols-outlined text-base">call_split</span>
                        {loadingDivision ? 'Dividiendo...' : 'Dividir impagas'}
                      </button>
                    </div>

                    {(previewDivision?.periodos || []).length > 0 && (
                      <div className="mt-3 grid grid-cols-1 md:grid-cols-2 gap-2">
                        {previewDivision.periodos.slice(0, 4).map((periodo) => (
                          <div key={periodo.boletaId} className="flex items-center justify-between rounded-md bg-white dark:bg-background-dark px-3 py-2 border border-gray-100 dark:border-gray-700">
                            <span className="text-sm font-medium text-[#111618] dark:text-white">{periodo.mes}/{periodo.anio}</span>
                            <span className="text-sm text-[#617c89] dark:text-gray-400">
                              {formatearDecimal(periodo.consumoOriginal)} m³ · {(previewDivision?.participantes?.length || vinculadosMedidor.length + 1)} partes
                            </span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Contacto */}
              <div className="bg-white dark:bg-background-dark dark:border dark:border-gray-700 rounded-xl shadow-sm p-6">
                <h2 className="text-lg font-bold text-[#111618] dark:text-white mb-4 flex items-center gap-2">
                  <span className="material-symbols-outlined text-primary">phone</span>
                  Contacto
                </h2>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-xs text-[#617c89] dark:text-gray-400 uppercase mb-1">WhatsApp</p>
                    <p className="text-sm font-medium text-[#111618] dark:text-white">{cliente.whatsapp || '-'}</p>
                  </div>
                  <div>
                    <p className="text-xs text-[#617c89] dark:text-gray-400 uppercase mb-1">Teléfono</p>
                    <p className="text-sm font-medium text-[#111618] dark:text-white">{cliente.telefono || '-'}</p>
                  </div>
                </div>
              </div>

              {/* Últimas Lecturas */}
              <div className="bg-white dark:bg-background-dark dark:border dark:border-gray-700 rounded-xl shadow-sm p-6">
                <h2 className="text-lg font-bold text-[#111618] dark:text-white mb-4 flex items-center gap-2">
                  <span className="material-symbols-outlined text-primary">speed</span>
                  Últimas Lecturas
                </h2>
                {lecturas.length === 0 ? (
                  <p className="text-center text-[#617c89] py-4">No hay lecturas registradas</p>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead className="border-b dark:border-gray-700">
                        <tr>
                          <th className="text-left text-xs font-medium text-[#617c89] uppercase py-2">Fecha</th>
                          <th className="text-right text-xs font-medium text-[#617c89] uppercase py-2">Lectura</th>
                          <th className="text-right text-xs font-medium text-[#617c89] uppercase py-2">Consumo m³</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y dark:divide-gray-700">
                        {lecturas.slice(0, 5).map((lectura) => (
                          <tr key={lectura.id}>
                            <td className="py-3 text-sm text-[#111618] dark:text-white">
                              {lectura.fechaLectura
                                ? formatApiDate(lectura.fechaLectura)
                                : '-'}
                            </td>
                            <td className="py-3 text-sm text-right font-medium text-[#111618] dark:text-white">
                              {lectura.lecturaActual}
                            </td>
                            <td className="py-3 text-sm text-right font-bold text-primary">
                              {lectura.consumoM3 || '-'}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </div>

            {/* Sidebar derecho */}
            <div className="space-y-6">
              {/* Estado */}
              <div className="bg-white dark:bg-background-dark dark:border dark:border-gray-700 rounded-xl shadow-sm p-6">
                <h3 className="text-sm font-medium text-[#617c89] dark:text-gray-400 mb-2">Estado</h3>
                {cliente.servicio_dado_de_baja ? (
                  <div className="space-y-1">
                    <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-red-100 text-red-700">
                      Servicio dado de baja
                    </span>
                    {cliente.fecha_baja_servicio && (
                      <p className="text-xs text-[#617c89] dark:text-gray-400">
                        Desde: {formatApiDate(cliente.fecha_baja_servicio)}
                      </p>
                    )}
                  </div>
                ) : cliente.activo !== false ? (
                  <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-active-green/10 text-active-green">
                    Activo
                  </span>
                ) : (
                  <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-inactive-gray/10 text-inactive-gray">
                    Inactivo
                  </span>
                )}
              </div>

              {/* Resumen de Cuenta */}
              <div className="bg-white dark:bg-background-dark dark:border dark:border-gray-700 rounded-xl shadow-sm p-6">
                <h3 className="text-sm font-medium text-[#617c89] dark:text-gray-400 mb-4">Resumen de Cuenta</h3>
                <div className="space-y-3">
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-[#111618] dark:text-white">Total Boletas</span>
                    <span className="text-lg font-bold text-[#111618] dark:text-white">{boletas.length}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-[#111618] dark:text-white">Pendientes</span>
                    <span className="text-lg font-bold text-debt-red">
                      {boletas.filter(b => b.estado === 'pendiente').length}
                    </span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-[#111618] dark:text-white">Pagadas</span>
                    <span className="text-lg font-bold text-active-green">
                      {boletas.filter(b => b.estado === 'pagada').length}
                    </span>
                  </div>
                </div>
              </div>

              {/* Acciones Rápidas */}
              <div className="bg-white dark:bg-background-dark dark:border dark:border-gray-700 rounded-xl shadow-sm p-6">
                <h3 className="text-sm font-medium text-[#617c89] dark:text-gray-400 mb-4">Acciones Rápidas</h3>
                <div className="space-y-2">
                  <button
                    onClick={() => navigate(`/administrativo/clientes/${id}/boletas`)}
                    className="w-full flex items-center gap-3 px-4 py-3 bg-[#f0f3f4] dark:bg-gray-700 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
                  >
                    <span className="material-symbols-outlined text-primary">receipt</span>
                    <span className="text-sm font-medium text-[#111618] dark:text-white">Ver Boletas</span>
                  </button>
                  {cliente.medidor && !cliente.servicio_dado_de_baja && (
                    <button
                      onClick={() => navigate(`/administrativo/clientes/${id}/nueva-lectura`)}
                      className="w-full flex items-center gap-3 px-4 py-3 bg-primary text-white rounded-lg hover:bg-primary/90 transition-colors"
                    >
                      <span className="material-symbols-outlined">speed</span>
                      <span className="text-sm font-medium">Nueva Lectura</span>
                    </button>
                  )}
                  {!cliente.servicio_dado_de_baja && (cliente.medidor ? (
                    <button
                      onClick={() => setShowModalMedidorRoto(true)}
                      className="w-full flex items-center gap-3 px-4 py-3 bg-error/10 text-error border border-error/20 rounded-lg hover:bg-error/20 transition-colors"
                    >
                      <span className="material-symbols-outlined">build_circle</span>
                      <span className="text-sm font-medium">Declarar Medidor Roto</span>
                    </button>
                  ) : (
                    <button
                      onClick={() => setShowModalAsignarMedidor(true)}
                      className="w-full flex items-center gap-3 px-4 py-3 bg-primary text-white rounded-lg hover:bg-primary/90 transition-colors"
                    >
                      <span className="material-symbols-outlined">add_circle</span>
                      <span className="text-sm font-medium">Asignar Medidor</span>
                    </button>
                  ))}
                  {cliente.servicio_dado_de_baja ? (
                    <button
                      onClick={handleReactivarServicio}
                      className="w-full flex items-center gap-3 px-4 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
                    >
                      <span className="material-symbols-outlined">power_settings_new</span>
                      <span className="text-sm font-medium">Reactivar servicio</span>
                    </button>
                  ) : (
                    <button
                      onClick={handleDarBajaServicio}
                      className="w-full flex items-center gap-3 px-4 py-3 bg-error/10 text-error border border-error/20 rounded-lg hover:bg-error/20 transition-colors"
                    >
                      <span className="material-symbols-outlined">link_off</span>
                      <span className="text-sm font-medium">Dar de baja servicio</span>
                    </button>
                  )}
                  <button className="w-full flex items-center gap-3 px-4 py-3 bg-[#f0f3f4] dark:bg-gray-700 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors">
                    <span className="material-symbols-outlined text-primary">print</span>
                    <span className="text-sm font-medium text-[#111618] dark:text-white">Imprimir Info</span>
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>

      {/* Modal Declarar Medidor Roto */}
      {showModalMedidorRoto && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-background-dark rounded-xl shadow-xl max-w-md w-full p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-xl font-bold text-[#111618] dark:text-white flex items-center gap-2">
                <span className="material-symbols-outlined text-error">build_circle</span>
                Declarar Medidor Roto
              </h3>
              <button
                onClick={() => setShowModalMedidorRoto(false)}
                className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
              >
                <span className="material-symbols-outlined">close</span>
              </button>
            </div>

            <div className="mb-4 p-4 bg-error/10 border border-error/20 rounded-lg">
              <p className="text-sm text-error font-medium mb-2">⚠️ Medidor Actual</p>
              <p className="text-sm text-[#617c89] dark:text-gray-400 mb-1">
                <strong>N° Serie:</strong> {cliente.medidor?.numeroSerie}
              </p>
              <p className="text-xs text-error mt-3 font-medium">
                Este medidor será dado de baja y el cliente quedará SIN medidor activo.
              </p>
            </div>

            <div className="mb-4 p-4 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-700 rounded-lg">
              <p className="text-sm text-amber-800 dark:text-amber-200 mb-2">
                <strong>ℹ️ Importante:</strong>
              </p>
              <ul className="text-xs text-amber-700 dark:text-amber-300 space-y-1 list-disc list-inside">
                <li>El cliente quedará sin medidor hasta que se asigne uno nuevo</li>
                <li>No se podrán registrar lecturas mientras no haya medidor</li>
                <li>Se le cobrará la <strong>tarifa mínima</strong> durante este período</li>
                <li>El servicio NO se cortará</li>
              </ul>
            </div>

            <div className="flex gap-3 mt-6">
              <button
                onClick={handleDeclararMedidorRoto}
                className="flex-1 h-12 px-4 bg-error text-white rounded-lg font-bold hover:bg-error/90 transition-colors"
              >
                Sí, Declarar Roto
              </button>
              <button
                onClick={() => setShowModalMedidorRoto(false)}
                className="h-12 px-4 border border-gray-300 dark:border-gray-600 text-[#111618] dark:text-white rounded-lg font-medium hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
              >
                Cancelar
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal Asignar Medidor */}
      {showModalAsignarMedidor && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-background-dark rounded-xl shadow-xl max-w-md w-full p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-xl font-bold text-[#111618] dark:text-white flex items-center gap-2">
                <span className="material-symbols-outlined text-primary">add_circle</span>
                Asignar Nuevo Medidor
              </h3>
              <button
                onClick={() => setShowModalAsignarMedidor(false)}
                className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
              >
                <span className="material-symbols-outlined">close</span>
              </button>
            </div>

            <div className="mb-4 p-4 bg-primary/10 border border-primary/20 rounded-lg">
              <p className="text-sm text-primary font-medium mb-1">
                ℹ️ Nuevo medidor para: {cliente.nombre}
              </p>
              <p className="text-xs text-[#617c89] dark:text-gray-400">
                Una vez asignado, se podrán registrar lecturas para este cliente
              </p>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-[#111618] dark:text-white mb-2">
                  Número de Serie *
                </label>
                <input
                  type="text"
                  value={nuevoMedidor.numeroSerie}
                  onChange={(e) => setNuevoMedidor({...nuevoMedidor, numeroSerie: e.target.value})}
                  className="w-full h-12 px-4 rounded-lg bg-[#f0f3f4] dark:bg-gray-700 border-none text-[#111618] dark:text-white placeholder:text-[#617c89] dark:placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-primary"
                  placeholder="Ej: MED-001"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-[#111618] dark:text-white mb-2">
                  Lectura Inicial (m³)
                </label>
                <input
                  type="number"
                  value={nuevoMedidor.lecturaInicial}
                  onChange={(e) => setNuevoMedidor({...nuevoMedidor, lecturaInicial: e.target.value})}
                  className="w-full h-12 px-4 rounded-lg bg-[#f0f3f4] dark:bg-gray-700 border-none text-[#111618] dark:text-white placeholder:text-[#617c89] dark:placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-primary"
                  placeholder="0"
                  min="0"
                />
                <p className="text-xs text-[#617c89] dark:text-gray-400 mt-1">
                  💡 Normalmente 0 para medidores nuevos. Puede ingresar otro valor si es necesario.
                </p>
              </div>
            </div>

            <div className="flex gap-3 mt-6">
              <button
                onClick={handleAsignarMedidor}
                className="flex-1 h-12 px-4 bg-primary text-white rounded-lg font-bold hover:bg-primary/90 transition-colors"
              >
                Asignar Medidor
              </button>
              <button
                onClick={() => setShowModalAsignarMedidor(false)}
                className="h-12 px-4 border border-gray-300 dark:border-gray-600 text-[#111618] dark:text-white rounded-lg font-medium hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
              >
                Cancelar
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal de Validación/Error/Éxito */}
      <Modal
        isOpen={modalConfig.isOpen}
        onClose={() => setModalConfig({ ...modalConfig, isOpen: false })}
        type={modalConfig.type}
        title={modalConfig.title}
        message={modalConfig.message}
        confirmText={modalConfig.confirmText}
        onConfirm={modalConfig.onConfirm}
        onCancel={modalConfig.onCancel}
      />
    </div>
  );
}

export default VerCliente;
